import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { ScrollDispatcher } from "@angular/cdk/scrolling";
import { Overlay } from "@angular/cdk/overlay";
import { Subject, takeUntil } from "rxjs";

import { LgLocalizationModule, useTranslationNamespace } from "@logex/framework/lg-localization";
import { LgOverlayService, UiCoreModule } from "@logex/framework/ui-core";

import {
    FeatureBenchmarkingService,
    IComparisonType,
    IConnectProvider,
    Provider,
} from "@codman/shared/feature-benchmarking";
import { OverlayBase } from "@codman/shared/ui-overlay";

import {
    ComparisonPopupInfoClickEvent,
    ComparisonPopupInfoInitProps,
    SharedComparisonPopupInfoComponent,
} from "../comparison-picker-popup-info/comparison-picker-popup-info.component";

const defaultProvider: Provider = "ownProvider";

@Component({
    selector: "codman-comparison-picker-button-info",
    templateUrl: "./comparison-picker-button-info.component.html",
    styleUrls: ["./comparison-picker-button-info.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, LgLocalizationModule, UiCoreModule],
    providers: [useTranslationNamespace("APP._Shared.ComparisonPicker")],
})
export class SharedComparisonPickerButtonInfoComponent extends OverlayBase<
    ComparisonPopupInfoInitProps,
    ComparisonPopupInfoClickEvent
> {
    private _benchmarkingService = inject(FeatureBenchmarkingService);

    @Input() text: string = "";
    @Input() fullText: string = "";
    @Input() type: Provider = defaultProvider;
    @Input() numerator?: number = 0;
    @Input() denominator?: number = 0;
    @Input() analysisUnit?: string = "";
    @Input() selectedConnectProviders: number[] | null | undefined;
    @Input() availableConnectProviders: IConnectProvider[] | null = [];
    @Input() linkType = false;
    @Input() showCloseIcon = false;
    @Input() comparisonType: IComparisonType | null = "basic";
    @Input() multiDataIndex: number = 0;

    @Output() readonly clickEvent = new EventEmitter<ComparisonPopupInfoClickEvent>();
    @Output() readonly closeEvent = new EventEmitter<Provider>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        _elementRef: ElementRef,
        _scrollDispatcher: ScrollDispatcher,
        _overlayService: LgOverlayService,
        _overlay: Overlay,
    ) {
        super(
            _elementRef,
            _scrollDispatcher,
            _overlayService,
            _overlay,
            SharedComparisonPopupInfoComponent,
        );
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        super._onDestroy();
    }

    _getTypeTitle(): string {
        switch (this.type) {
            case "connectBenchmark":
                return ".Connect.Title";
            default:
                return this.text;
        }
    }

    _close(event: Event): void {
        event.stopPropagation();
        this.closeEvent.emit(this.type);
    }

    _openDialog(): void {
        this._showPopup(
            {
                patientsCount: {
                    numerator: this.numerator,
                    denominator: this.denominator,
                },
                analysisUnit: this.analysisUnit,
                showPatientList:
                    this.type === defaultProvider && this.comparisonType !== "multiData",
                showTrend: this.type === defaultProvider,
                showConnect: this.type === "connectBenchmark",
                availableConnectProviders: this.availableConnectProviders ?? [],
                selectedConnectProviders: this.selectedConnectProviders ?? [],
                title: this._getTypeTitle(),
                color:
                    this.comparisonType === "multiData"
                        ? this._benchmarkingService.colorsOrdered[this.multiDataIndex + 1].medium
                        : this._benchmarkingService.colors[this.type],
                comparisonType: this.comparisonType,
                multiDataIndex: this.multiDataIndex,
            },
            {
                positions: ["bottom-left", "bottom-center"],
            },
        )
            .pipe(takeUntil(this._destroy$))
            .subscribe(
                event => this.clickEvent.emit(event),
                err => console.error(err),
                () => this._hidePopup(),
            );
    }
}
